import { Box, Typography, useTheme } from '@mui/material';

import { HeadingTag, Maybe, TAGContentAlignment } from '@/types';
import {
	ServiceLineHeroButtonCollection,
	ServiceLineHeroSubHeadline,
	ServiceLineHeroBodyCopy,
	ServiceLineHeroHeadline,
	ContentTypeRichTextContent,
	TermsAndConditions as TTermsAndConditions,
	AnalyticsContext,
	ServiceLineHeroContentListCollection,
	ServiceLineHeroContentListItem,
} from '@/types/generated';
import TermsAndConditions from '@/components/TermsAndConditions';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import { customEvent } from '@/utils';
import ContentfulButton from '@/components/ContentfulButton';
import resolveContentfulHeadingTag from '@/utils/resolveContentfulHeadingTag';
import Accordions, { IAccordions } from '@/components/Accordions/Accordions';
import SingleMessage from '@/components/SingleMessage';
import Table from '@/components/Table';
import { resolveTypographyVariantStyles } from '@/utils/resolveTypographyVariantStyles';

import OfficeInfo from '../OfficeInfo/OfficeInfo';

import {
	wrapper,
	textContainerStyles,
	ctaButtonWrapperStyles,
	TACWrapperStyles,
	contentListWrapperStyles,
} from './ServiceLineHeroContent.styles';

export interface IServiceLineHeroContent {
	headline?: Maybe<ServiceLineHeroHeadline>;
	subHeadline?: Maybe<ServiceLineHeroSubHeadline>;
	bodyCopy?: Maybe<ServiceLineHeroBodyCopy>;
	contentListItems?: ServiceLineHeroContentListCollection['items'];
	buttons?: ServiceLineHeroButtonCollection['items'];
	termsConditions?: Maybe<TTermsAndConditions>;
	analyticsContext?: Maybe<AnalyticsContext>;
	parentBackgroundColor?: Maybe<string>;
	isDarkBackground: boolean;
	contentAlign?: Maybe<TAGContentAlignment>;
	eyebrow?: Maybe<string>;
	eyebrowHtag?: Maybe<HeadingTag>;
	showOfficeDetails?: Maybe<boolean>;
	googleMapsBusinessQuery?: Maybe<string>;
	displayOfficeHoursInModal?: Maybe<boolean>;
	officeHoursLabel?: string;
	narrowLayout?: boolean;
}

const analyticsClick = (context: Maybe<AnalyticsContext> | undefined) => {
	if (context) {
		customEvent('tagEvent', context, context.eventName);
	}
};

const ContentListItemByType = (props: {
	contentItem: ServiceLineHeroContentListItem;
	contentAlign?: Maybe<string>;
	backgroundColor?: Maybe<string>;
}) => {
	const { contentItem, contentAlign, backgroundColor } = props;
	const type = contentItem?.__typename;
	if (!type) return null;
	const centerAligned = contentAlign === 'Center';

	switch (type) {
		case 'ContentTypeRichText':
			return (
				<ContentTypeRichText
					{...contentItem}
					disableGutters={true}
					centerAlign={centerAligned}
					backgroundColor={backgroundColor}
				/>
			);
		case 'SingleMessage':
			return (
				<SingleMessage
					{...contentItem}
					disableGutters={true}
					contentAlign={contentAlign}
					backgroundColor={backgroundColor}
				/>
			);
		case 'Table':
			return (
				<Table
					{...contentItem}
					containerSx={{ paddingX: 0 }}
					titleHtag={contentItem.titleHtag as HeadingTag}
					backgroundColor={backgroundColor}
				/>
			);
		case 'Accordions':
			return (
				<Accordions
					{...(contentItem as IAccordions)}
					titleAlignment={contentAlign}
					disableGutters={true}
					backgroundColor={backgroundColor}
				/>
			);

		default:
			return null;
	}
};

export default function ServiceLineHeroContent({
	headline,
	eyebrowHtag = 'normal',
	eyebrow,
	subHeadline,
	bodyCopy,
	contentListItems,
	buttons,
	termsConditions,
	isDarkBackground = false,
	contentAlign = 'Left',
	showOfficeDetails = false,
	googleMapsBusinessQuery,
	displayOfficeHoursInModal = false,
	parentBackgroundColor,
	officeHoursLabel,
	narrowLayout = false,
}: Readonly<IServiceLineHeroContent>) {
	const theme = useTheme();
	const centerAligned = contentAlign === 'Center';

	// Resolve text colors based on background
	let titleColor: string;
	let secondaryColor: string;
	let bodyColorProp: { color?: string };
	if (isDarkBackground) {
		titleColor = 'text.light';
		secondaryColor = 'text.light';
		bodyColorProp = { color: 'text.light' };
	} else {
		titleColor = 'text.primary';
		secondaryColor = 'text.secondary';
		bodyColorProp = {};
	}

	return (
		<Box data-testid="section_content_service_line_hero" sx={wrapper(centerAligned, narrowLayout)}>
			<Box sx={textContainerStyles(centerAligned)} data-testid={'service_line_hero_content_container'}>
				{eyebrow && (
					<Typography
						data-testid="service_line_hero_eyebrow_text"
						variant="overline"
						component={resolveContentfulHeadingTag(eyebrowHtag as HeadingTag)}
						color={secondaryColor}
					>
						{eyebrow}
					</Typography>
				)}
				{headline && (
					<ContentTypeRichText
						centerAlign={centerAligned}
						disableGutters
						disablePaddingOn={['Top', 'Bottom']}
						contentMaxWidth="100%"
						backgroundColor="transparent"
						dataTestId="group_service_line_hero_headline"
						content={headline as ContentTypeRichTextContent}
						docProps={{
							margin: 0,
							sx: [
								resolveTypographyVariantStyles(theme, 'heroMedium'),
								{
									[theme.breakpoints.up('md')]: {
										...resolveTypographyVariantStyles(theme, 'heroLarge'),
										color: titleColor,
									},
								},
								{
									color: titleColor,
								},
							],
						}}
					/>
				)}

				{subHeadline && (
					<ContentTypeRichText
						centerAlign={centerAligned}
						disableGutters
						disablePaddingOn={['Top', 'Bottom']}
						contentMaxWidth="100%"
						backgroundColor="transparent"
						dataTestId="group_service_line_hero_sub_headline"
						content={subHeadline as ContentTypeRichTextContent}
						docProps={{
							margin: 0,
							color: secondaryColor,
							variant: 'header3',
						}}
					/>
				)}

				{bodyCopy && (
					<ContentTypeRichText
						centerAlign={centerAligned}
						disableGutters
						disablePaddingOn={['Top', 'Bottom']}
						contentMaxWidth="100%"
						dataTestId="group_service_line_hero_body_copy"
						backgroundColor="transparent"
						content={bodyCopy as ContentTypeRichTextContent}
						docProps={{
							variant: 'bodyMediumBook',
							...bodyColorProp,
						}}
					/>
				)}

				{showOfficeDetails && (
					<OfficeInfo
						googleMapsBusinessQuery={googleMapsBusinessQuery}
						displayOfficeHoursInModal={displayOfficeHoursInModal}
						officeHoursLabel={officeHoursLabel}
					/>
				)}

				{contentListItems && contentListItems?.length > 0 && (
					<Box data-testid="group_service_line_hero_contentListItems" sx={contentListWrapperStyles}>
						{contentListItems.map((item, index) =>
							item ? (
								<ContentListItemByType
									contentItem={item}
									key={`${item?.sys?.id || ''}-${index}`}
									contentAlign={contentAlign}
									backgroundColor={parentBackgroundColor}
								/>
							) : null
						)}
					</Box>
				)}

				{buttons && buttons.length > 0 && (
					<Box data-testid="group_service_line_hero_cta_container" sx={ctaButtonWrapperStyles(centerAligned)}>
						{buttons.map((button) =>
							button ? (
								<ContentfulButton
									key={button.sys.id}
									{...button}
									dataTestId="service_line_hero_cta"
									onClick={() => analyticsClick(button.analyticsContext)}
								/>
							) : null
						)}
					</Box>
				)}

				{termsConditions && (
					<Box data-testid="group_service_line_hero_toc_container" sx={TACWrapperStyles}>
						<TermsAndConditions
							{...termsConditions}
							textColor={isDarkBackground ? 'text.light' : 'text.secondary'}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
}
